/**
 * Creates a tag reference object with the provided parameters.
 *
 * @param {object} user - The user who created the tag reference. Optional.
 * @param {number} damsId - The DAMS ID of the user who created the tag reference. Optional.
 * @param {number} id - The unique identifier for the tag reference. Required.
 * @param {string} uniqueId - A unique string identifier for the tag reference. Required.
 * @param {string} locale - The locale for the tag reference (e.g., 'en', 'fr'). Required.
 * @param {string} title - The title of the tag reference. Must be a non-empty string. Required.
 * @param {string} source - The source of the tag reference. Optional.
 * @param {Object} content - Additional content for the tag reference. Optional.
 * @param {string} publishingType - The type of publishing for the tag reference. Optional.
 * @returns {Object} An object containing the tag reference details.
 * @throws {Error} Throws an error if `id`, `uniqueId`, `locale`, or `title` are missing or invalid.
 */
export const createTagReference = (user,
                                   damsId,
                                   id,
                                   uniqueId,
                                   locale,
                                   title,
                                   source,
                                   content,
                                   publishingType) => {

    if (!user) {
        throw new Error('User is required to create a tag reference.');
    }

    if (!id) {
        throw new Error("ID is required to create a tag reference.");
    }

    if (!uniqueId) {
        throw new Error('Unique ID is required to create a tag reference.');
    }

    if (!locale) {
        throw new Error("Locale is required to create a tag reference.");
    }

    if (typeof title !== 'string' || title.trim() === '') {
        throw new Error("Title must be a non-empty string.");
    }

    const initiator = {
        "createdById": damsId,
        "createdByName": user.name,
        "createdAt": new Date(),
    };

    const lastStatusPropertyName = publishingType.replace('publishing-', '');

    const referenceObj = {
        id: id,
        uniqueId: uniqueId,
        locale: locale,
        _action: 'put',
        title: title,
        source: source,
        sourceId: null,
        content: content || {}
    };

    const obj = {
        reference: referenceObj,
    };

    // Add additional information for non-foldertag publishing types,
    // including initiator details and sync status.
    if (publishingType !== 'foldertag') {
        obj.initiator = initiator;
        obj.publishingType = publishingType;
        obj.lastStatus = `pending-${lastStatusPropertyName}`;
        obj.sync = {
            phases: [
                {status: 'pending', createdAt: new Date()},
            ],
            createdAt: new Date()
        };
    }

    return obj;
};